import React, { useRef } from 'react'
import BaseQuestion from '../../base-question'
import s from '../../styles.module.css'

interface InsuranceMembershipIdQuestionZustandProps {
  question: {
    id: number | string
    text: string
    isRequired?: boolean
    type: string
    placeholder?: string
    responses?: Array<{
      id: number | string
      text: string
    }>
  }
  value?: string | null
  skipValue?: boolean
  error?: string
  onChange?: (value: string | null, metadata?: Record<string, any>) => void
  onSkipChange?: (skipValue: boolean) => void
  questionId: number
}

const InsuranceMembershipIdQuestionZustand: React.FC<
  InsuranceMembershipIdQuestionZustandProps
> = ({
  question,
  value,
  skipValue,
  error,
  onChange,
  onSkipChange,
  questionId,
}) => {
  const initialLoad = useRef(true)
  const valueBeforeSkip = useRef<string | null>(null)

  const handleTextBlur = (textValue: string) => {
    if (initialLoad.current) {
      initialLoad.current = false
      return
    }
    // Clear skip when user enters text
    if (textValue && skipValue) {
      onSkipChange?.(false)
    }
    onChange?.(textValue, { skipMembershipId: false })
  }

  const handleSkipChange = (checked: boolean) => {
    if (checked) {
      // Store current value before clearing
      valueBeforeSkip.current = value || null
      // Clear the text value when skip is checked
      onChange?.(null, { skipMembershipId: true })
    } else {
      // Restore value when unchecking skip
      if (valueBeforeSkip.current) {
        onChange?.(valueBeforeSkip.current, { skipMembershipId: false })
      }
    }
    onSkipChange?.(checked)
  }

  return (
    <BaseQuestion
      question={{
        ...question,
        id: questionId,
      }}
      error={error}>
      <div>
        <input
          type="text"
          className={s.textInput}
          value={value || ''}
          onChange={e => {
            onChange?.(e.target.value)
            // Clear skip when user types
            if (e.target.value && skipValue) {
              onSkipChange?.(false)
            }
          }}
          onBlur={e => handleTextBlur(e.target.value)}
          placeholder={question.placeholder || 'Enter membership ID'}
          disabled={skipValue}
        />
        {question.responses && question.responses.length > 0 && (
          <div className={s.checkboxContainer}>
            {question.responses.map(response => (
              <label key={response.id} className={s.checkboxLabel}>
                <input
                  type="checkbox"
                  className={s.checkbox}
                  checked={skipValue || false}
                  onChange={e => handleSkipChange(e.target.checked)}
                />
                <span className={s.checkboxText}>{response.text}</span>
              </label>
            ))}
          </div>
        )}
        {skipValue && (
          <div className={s.skipWarning}>
            We'll need your Member ID to confirm your benefits before your first
            visit. Grabbing your insurance card now can save you a step later —
            but you can share it with your coach after scheduling your first
            call if you don't have it on hand at the moment.
          </div>
        )}
      </div>
    </BaseQuestion>
  )
}

export default InsuranceMembershipIdQuestionZustand
